/* eslint-disable react/no-array-index-key */
/* eslint-disable react/no-danger */
import React, { ReactElement, useEffect } from "react";
import ReactDOM from "react-dom/client";
import { Image } from "@wss/image";
import { BuyingGuideSection } from "./Types/BuyingGuideSection";
import BuyingGuideTable from "./Components/BuyingGuideTable";
import BuyingGuideTextImage from "./Components/BuyingGuideTextImage";
import BuyingGuideTextImageHighlight from "./Components/BuyingGuideTextImageHighlight";
import BuyingGuideImageFull from "./Components/BuyingGuideImageFull";
import BuyingGuideVideo from "./Components/BuyingGuideVideo";
import BuyingGuideTableGrouping from "./Components/BuyingGuideTableGrouping";
import BuyingGuideProAndConList from "./Components/BuyingGuideProAndConList";
import ContentLegalDisclaimer from "~/common/ContentLegalDisclaimer/ContentLegalDisclaimer";
import { SocialMediaShareData } from "~/common/SocialMediaShare/SocialMediaShareData";
import BuyingGuideSectionTypes from "./Types/BuyingGuideSectionTypes";
import ProfitCalculator from "~/ProductDetails/Templates/ExpandedDescriptionTemplateDetailSection/ContentSection/ProfitCalculator";
import { addHeaderTagIfMissing, shouldRenderDiv } from "./buyingGuideHelpers";
import HandDryerCalculator from "./HandDryerCalculator/HandDryerCalculator";
import PostTitleSection from "../Shared/Components/PostTitleSection";

interface Props {
  guide: {
    title: string;
    imagePath: string;
    sections: BuyingGuideSection[];
    breadcrumbCategory: string;
    dateModified: string;
    displayHeader: string;
    introBlockHtml: string;
    relatedResources: {
      header: string;
      imagePath: string;
      content: string;
      link: string;
    }[];
    socialMediaButtons: SocialMediaShareData[];
  };
  displayHeader: string;
  topicUrl: string;
}

export default function FoodServiceResourcesBuyingGuide({
  guide,
  displayHeader,
  topicUrl,
}: Readonly<Props>) {
  useEffect(() => {
    const calculatorDiv = document.getElementById("profitCalculator");
    const title = calculatorDiv?.dataset.title || undefined;
    const cost = calculatorDiv?.dataset.cost || undefined;
    const price = calculatorDiv?.dataset.price || undefined;
    const serving = calculatorDiv?.dataset.serving || undefined;

    if (calculatorDiv) {
      const root = ReactDOM.createRoot(calculatorDiv);
      root.render(
        <ProfitCalculator
          defaultTitle={title}
          defaultCost={cost}
          defaultPrice={price}
          defaultServings={serving}
        />
      );
    }
  }, []);

  useEffect(() => {
    const calculatorDiv = document.getElementById("handDryerCalculator");

    if (calculatorDiv) {
      const root = ReactDOM.createRoot(calculatorDiv);
      root.render(<HandDryerCalculator />);
    }
  }, []);

  const {
    introBlockHtml,
    imagePath,
    relatedResources,
    socialMediaButtons,
    sections,
  } = guide;

  return (
    <div data-module="foodServiceResourcesBuyingGuide">
      <div className="fsr-content-wrap fsr-guides">
        <div className="hero">
          <div id="guide-header" className="guide-header-container">
            <Image
              className="hero-image"
              src={imagePath}
              alt=""
              width={64}
              height={25}
              loading="eager"
            />
          </div>
        </div>

        <PostTitleSection
          {...{
            additionalHeaderClasses: "guide-header",
            categories: [{ name: guide.breadcrumbCategory, url: topicUrl }],
            containerID: "guide-title",
            dateModified: guide.dateModified,
            displayHeader,
            socialMediaButtons,
          }}
        />

        <div className="guide-kicker clearfix">
          <div id="guide-intro" className="guide-section">
            {shouldRenderDiv(introBlockHtml) && (
              <div
                dangerouslySetInnerHTML={{
                  __html: introBlockHtml,
                }}
              />
            )}
          </div>
        </div>

        <div className="section-container">
          {sections.map((section) =>
            section.type === BuyingGuideSectionTypes.SectionGrouping ? (
              <div
                className="guide-section-group"
                key={`section-container-${section.id}`}
              >
                {shouldRenderDiv(section.heading) && (
                  <div
                    className="guide-section-group__header"
                    dangerouslySetInnerHTML={{
                      __html: addHeaderTagIfMissing(section.heading, "h2"),
                    }}
                  />
                )}
                <DisplaySectionGroup
                  sections={section.children}
                  defaultHeaderTag={
                    shouldRenderDiv(section.heading) ? "h3" : "h2"
                  }
                />
              </div>
            ) : (
              <DisplaySectionGroup
                sections={[section]}
                defaultHeaderTag="h2"
                key={`section-container-${section.id}`}
              />
            )
          )}
        </div>
        <ContentLegalDisclaimer />
      </div>
      {relatedResources?.length > 0 && (
        <div className="fsr-content-wrap">
          <p className="header-2">Related Resources</p>
          <div className="fsr-flex">
            {relatedResources.map(
              ({ link, imagePath, header, content }, index) => (
                <a
                  href={link}
                  className="fsr-item fsr-flex"
                  key={`FsrArticleRelatedReource-${index}`}
                >
                  <span className="fsr-img">
                    <img src={imagePath} alt="" />
                  </span>
                  <span className="fsr-text">
                    <p className="resource-title">{header}</p>
                    <p className="resource-brief">{content}</p>
                  </span>
                </a>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DisplaySectionGroup({
  sections,
  defaultHeaderTag,
}: {
  sections: BuyingGuideSection[];
  defaultHeaderTag: string;
}): ReactElement {
  return (
    <>
      {sections.map((section, index) => (
        <GetSection
          section={section}
          defaultHeaderTag={defaultHeaderTag}
          key={`section-${index}`}
        />
      ))}
    </>
  );
}

function GetSection({
  section,
  defaultHeaderTag,
}: {
  section: BuyingGuideSection;
  defaultHeaderTag: string;
}): ReactElement {
  switch (section.type) {
    case BuyingGuideSectionTypes.GenericContent:
      return (
        <div className="guide-section">
          {section.heading?.length > 0 && (
            <div
              dangerouslySetInnerHTML={{
                __html: addHeaderTagIfMissing(
                  section.heading,
                  defaultHeaderTag
                ),
              }}
            />
          )}
          {shouldRenderDiv(section.contents[0]?.htmlContent) && (
            <div
              dangerouslySetInnerHTML={{
                __html: section.contents[0]?.htmlContent ?? "",
              }}
            />
          )}
        </div>
      );
    case BuyingGuideSectionTypes.TableSection:
      return (
        <div className="guide-section">
          <BuyingGuideTable
            section={section}
            defaultHeaderTag={defaultHeaderTag}
            enableHeading
          />
        </div>
      );
    case BuyingGuideSectionTypes.TextImageLeft:
    case BuyingGuideSectionTypes.TextImageRight:
      return (
        <BuyingGuideTextImage
          defaultHeaderTag={defaultHeaderTag}
          section={section}
        />
      );
    case BuyingGuideSectionTypes.DoubleImage:
    case BuyingGuideSectionTypes.TripleImage:
    case BuyingGuideSectionTypes.QuadrupleImage:
      return <BuyingGuideTextImageHighlight section={section} />;
    case BuyingGuideSectionTypes.FullImage:
      return <BuyingGuideImageFull section={section} />;
    case BuyingGuideSectionTypes.Video:
      return <BuyingGuideVideo section={section} />;
    case BuyingGuideSectionTypes.TableGrouping:
      return <BuyingGuideTableGrouping section={section} />;
    case BuyingGuideSectionTypes.ProConList:
      return <BuyingGuideProAndConList section={section} />;
    default:
      return <></>;
  }
}
